import React, { useState, useEffect } from 'react';
import { Grid, Card, Form, Table } from "tabler-react";
import SiteWrapper from "../../SiteWrapper.react";
import { Crud_Catalogos } from '../../herramientas/Crud_Catalogos';
import { Doughnut, HorizontalBar } from 'react-chartjs-2';
import Select from 'react-select';

/**
 * Tablero Captura Detalle
 * --------------------------------------------------------------------------
 * Módulo NUEVO e independiente. Coexiste con el tablero viejo
 * (TableroCapturaEnTiempo) sin modificarlo.
 *
 * Consume el endpoint nuevo del backend:
 *   informes/capturaEnTiempoDetalle/{fechaInicio}/{fechaFin}/{plataformas}
 * que devuelve un objeto:
 *   { movimientos: [...], porEventoLogistico: [...], general: {...} }
 *
 * Incluye:
 *   - Descarga CSV (una fila por movimiento, Opción A).
 *   - Gráfica de barras horizontales por evento logístico (desc) con %.
 *   - Filtro por evento logístico (afecta barras, circular y CSV).
 *   - Gráfica circular general.
 */
export const TableroCapturaDetalle = () => {
  const [plataformas, setPlataformas] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [mostrarFecha, setMostrarFecha] = useState(false);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  // Respuesta del endpoint
  const [movimientos, setMovimientos] = useState([]);
  const [porEvento, setPorEvento] = useState([]);
  const [general, setGeneral] = useState(null);

  // Desempeño por usuario (Tablero por ACV) — del endpoint existente capturaEnTiempo
  const [porUsuario, setPorUsuario] = useState([]);

  // Filtro por evento logístico
  const [eventoFiltro, setEventoFiltro] = useState(null);

  const [cargando, setCargando] = useState(false);
  const [sinDatos, setSinDatos] = useState(false);

  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    setFechaInicio(`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`);
    setFechaFin(`${tomorrow.getFullYear()}-${tomorrow.getMonth() + 1}-${tomorrow.getDate()}`);

    const fetchPlataformas = async () => {
      try {
        const plataformas1 = await getfuntion("get", [], "", "plataformas/plataforma/1", "");
        const plataformas3 = await getfuntion("get", [], "", "plataformas/plataforma/3", "");
        setPlataformas([...plataformas1, ...plataformas3]);
      } catch (error) {
        console.error("Error fetching plataformas:", error);
      }
    };
    fetchPlataformas();
  }, []);

  const getfuntion = async (metodo = "get", obj = [], id = "", catalogo = "", stateVar = "", hiddenModl = "") => {
    return Crud_Catalogos(catalogo, "movimientos", metodo, id, obj, "", stateVar, hiddenModl, [])
      .then((returnVal) => {
        if (metodo === "get") return returnVal;
        return null;
      });
  };

  const changeMostrarFecha = () => {
    const nuevo = !mostrarFecha;
    setMostrarFecha(nuevo);
    if (!nuevo) {
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);
      setFechaInicio(`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`);
      setFechaFin(`${tomorrow.getFullYear()}-${tomorrow.getMonth() + 1}-${tomorrow.getDate()}`);
    }
  };

  const options = plataformas.map((plataforma) => ({
    value: plataforma.id,
    label: plataforma.nombre,
  }));

  const handleSelectChange = (selected) => {
    setSelectedOptions(selected || []);
  };

  const parseRespuesta = (returnVal) => {
    // El backend devuelve el objeto serializado como String (sp -> varchar).
    // Crud_Catalogos puede entregarlo ya parseado o como string; soportamos ambos.
    if (!returnVal) return null;
    if (typeof returnVal === "string") {
      try {
        return JSON.parse(returnVal);
      } catch (e) {
        return null;
      }
    }
    return returnVal;
  };

  const handleButtonClick = async () => {
    const selectedIds = selectedOptions.map(option => option.value);
    if (selectedIds.length === 0) {
      setSinDatos(true);
      setMovimientos([]);
      setPorEvento([]);
      setGeneral(null);
      return;
    }

    const newUrl = `informes/capturaEnTiempoDetalle/${fechaInicio}/${fechaFin}/${selectedIds.join(",")}`;
    const urlUsuarios = `informes/capturaEnTiempo/${fechaInicio}/${fechaFin}/${selectedIds.join(",")}`;

    setCargando(true);
    setSinDatos(false);
    setEventoFiltro(null);
    setMovimientos([]);
    setPorEvento([]);
    setGeneral(null);
    setPorUsuario([]);

    try {
      // Se consultan ambos endpoints en paralelo:
      //  - capturaEnTiempoDetalle: movimientos + porEventoLogistico + general (módulo nuevo)
      //  - capturaEnTiempo: array por usuario (endpoint EXISTENTE, el mismo del tablero viejo)
      const [returnVal, returnUsuarios] = await Promise.all([
        getfuntion("get", [], "", newUrl, ""),
        getfuntion("get", [], "", urlUsuarios, ""),
      ]);

      const data = parseRespuesta(returnVal);

      const movs = (data && Array.isArray(data.movimientos)) ? data.movimientos : [];
      const eventos = (data && Array.isArray(data.porEventoLogistico)) ? data.porEventoLogistico : [];
      const gen = (data && data.general) ? data.general : null;

      // El endpoint capturaEnTiempo devuelve un array por usuario (o su JSON como string)
      const usuariosParse = parseRespuesta(returnUsuarios);
      const usuarios = Array.isArray(usuariosParse) ? usuariosParse : [];

      if (movs.length === 0 && eventos.length === 0 && usuarios.length === 0) {
        setSinDatos(true);
      } else {
        setMovimientos(movs);
        setPorEvento(eventos);
        setGeneral(gen);
        setPorUsuario(usuarios);
      }
    } catch (error) {
      console.error("Error consultando capturaEnTiempoDetalle:", error);
      setSinDatos(true);
    } finally {
      setCargando(false);
    }
  };

  // --------- Filtro por evento logístico ----------
  const opcionesEvento = porEvento.map((e) => ({ value: e.evento, label: e.evento }));

  const coincideEvento = (mov) => {
    if (!eventoFiltro) return true;
    const eventos = (mov.eventosLogisticos || "").split(";").map(s => s.trim());
    return eventos.includes(eventoFiltro.value);
  };

  const movimientosFiltrados = movimientos.filter(coincideEvento);
  const porEventoFiltrado = eventoFiltro
    ? porEvento.filter(e => e.evento === eventoFiltro.value)
    : porEvento;

  // Recalcular porcentajes sobre el subconjunto mostrado (para que sumen 100% al filtrar)
  const totalEventos = porEventoFiltrado.reduce((acc, e) => acc + (e.total || 0), 0);
  const barrasData = [...porEventoFiltrado]
    .sort((a, b) => (b.total || 0) - (a.total || 0));

  // --------- Datos de las gráficas ----------
  const dataBarras = {
    labels: barrasData.map(e => e.evento),
    datasets: [{
      label: "Actualizaciones por evento logístico",
      backgroundColor: "#467fcf",
      data: barrasData.map(e => e.total || 0),
    }]
  };

  const barrasOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    tooltips: {
      callbacks: {
        label: (tooltipItem) => {
          const total = tooltipItem.xLabel || 0;
          const pct = totalEventos ? ((100 * total) / totalEventos).toFixed(1) : 0;
          return ` ${total} (${pct}%)`;
        }
      }
    },
    scales: {
      xAxes: [{ ticks: { beginAtZero: true, precision: 0 } }]
    }
  };

  const generalData = general ? {
    labels: ["0-20 min", "20-40 min", ">40 min"],
    datasets: [{
      backgroundColor: ["#83ce83", "#f1c40f", "#f96a5d"],
      data: [general.on_time || 0, general.middle_time || 0, general.out_time || 0]
    }]
  } : null;

  // --------- TABLERO POR ACV (desempeño por usuario, barras apiladas) ----------
  // Clona los datos del endpoint existente capturaEnTiempo (array por usuario).
  // Mapea "humberto.munoa" -> "sistema" igual que el tablero viejo.
  const nombreUsuario = (usr) =>
    usr.username === "humberto.munoa" ? "sistema" : usr.username;

  const totalGlobalAcv = porUsuario.reduce(
    (acc, u) => acc + (u.on_time || 0) + (u.middle_time || 0) + (u.out_time || 0), 0
  );

  // Orden descendente por total del usuario (equivale a orden desc por %)
  const acvOrdenado = [...porUsuario].sort((a, b) => {
    const ta = (a.on_time || 0) + (a.middle_time || 0) + (a.out_time || 0);
    const tb = (b.on_time || 0) + (b.middle_time || 0) + (b.out_time || 0);
    return tb - ta;
  });

  const acvLabels = acvOrdenado.map(nombreUsuario);
  const acvPorcentajes = acvOrdenado.map((u) => {
    const t = (u.on_time || 0) + (u.middle_time || 0) + (u.out_time || 0);
    return totalGlobalAcv ? (100 * t) / totalGlobalAcv : 0;
  });

  const dataAcv = {
    labels: acvLabels,
    datasets: [
      { label: "0-20 min", backgroundColor: "#83ce83", data: acvOrdenado.map(u => u.on_time || 0), stack: "acv" },
      { label: "20-40 min", backgroundColor: "#f1c40f", data: acvOrdenado.map(u => u.middle_time || 0), stack: "acv" },
      { label: ">40 min", backgroundColor: "#f96a5d", data: acvOrdenado.map(u => u.out_time || 0), stack: "acv" },
    ]
  };

  const acvOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { position: "top" },
    tooltips: {
      mode: "index",
      callbacks: {
        afterTitle: (items) => {
          const idx = items && items.length ? items[0].index : -1;
          return idx >= 0 ? `Total: ${((acvPorcentajes[idx]) || 0).toFixed(2)}%` : "";
        }
      }
    },
    scales: {
      xAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 } }],
      yAxes: [{ stacked: true }],
    }
  };

  // --------- Descarga CSV ----------
  const descargarCSV = () => {
    const encabezados = [
      "ID", "PO", "Booking", "Contenedor", "GPS",
      "Fecha y hora de captura de GPS", "Tipo de movimiento", "Cliente",
      "Geocerca registrada en SkyOne", "Rango de tiempo en que fue actualizado",
      "Usuario por el que fue actualizado", "Evento logistico al que fue actualizado"
    ];

    const escapar = (valor) => {
      const s = (valor === null || valor === undefined) ? "" : String(valor);
      if (s.includes('"') || s.includes(",") || s.includes("\n")) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };

    const filas = movimientosFiltrados.map((m) => [
      m.id,
      m.po,
      m.booking,
      m.contenedor,
      m.gps,
      m.fechaCapturaGps,
      m.tipoMovimiento,
      m.cliente,
      m.geocercaSkyone ? "Si" : "No",
      m.rangosTiempo,
      m.usuarios,
      m.eventosLogisticos,
    ].map(escapar).join(","));

    const contenido = "\uFEFF" + [encabezados.join(","), ...filas].join("\n");
    const blob = new Blob([contenido], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `tablero_captura_detalle_${fechaInicio}_${fechaFin}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <SiteWrapper>
      <div className="container">
        <h1>Tablero Captura Detalle</h1>

        <div>
          Tiempo Actual
          <Form.Switch
            type="Checkbox"
            name="toggle"
            value={mostrarFecha}
            checked={mostrarFecha}
            onChange={changeMostrarFecha}
          /> Histórico

          {mostrarFecha ? (
            <div>
              Fecha Inicio
              <input
                onChange={(opcion) => setFechaInicio(opcion.target.value)}
                type="date"
                id="fechaInicio"
                name="fechaInicio"
              />
              Fecha Fin
              <input
                onChange={(opcion) => setFechaFin(opcion.target.value)}
                type="date"
                id="fechaFin"
                name="fechaFin"
              />
            </div>
          ) : null}

          <Select
            isMulti
            options={options}
            hideSelectedOptions
            placeholder="Selecciona la plataforma"
            onChange={handleSelectChange}
          />
          <button
            onClick={handleButtonClick}
            className="mt-4 p-2 bg-blue-500 text-white rounded-lg shadow">
            Mostrar Selección
          </button>
        </div>

        {cargando ? (
          <p style={{ textAlign: "center", marginTop: "20px" }}>Cargando...</p>
        ) : sinDatos ? (
          <p style={{ textAlign: "center", fontSize: "18px", color: "red", marginTop: "20px" }}>
            La plataforma no tiene datos
          </p>
        ) : (movimientos.length > 0 || porEvento.length > 0 || porUsuario.length > 0) ? (
          <>
            {/* Controles: filtro por evento logístico + descarga CSV */}
            <Grid.Row className="mt-4">
              <Grid.Col sm={12} lg={6}>
                <label>Filtrar por evento logístico</label>
                <Select
                  isClearable
                  options={opcionesEvento}
                  value={eventoFiltro}
                  placeholder="Todos los eventos"
                  onChange={(sel) => setEventoFiltro(sel)}
                />
              </Grid.Col>
              <Grid.Col sm={12} lg={6} className="d-flex align-items-end justify-content-end">
                <button
                  onClick={descargarCSV}
                  className="mt-2 p-2 bg-blue-500 text-white rounded-lg shadow">
                  Descargar CSV
                </button>
              </Grid.Col>
            </Grid.Row>

            {/* Barras horizontales por evento logístico (desc, con %) */}
            <Grid.Col sm={12} lg={12}>
              <Card title="Actualización de evento logístico">
                <Card.Body>
                  <div style={{ height: Math.max(220, barrasData.length * 38) + "px" }}>
                    {barrasData.length > 0 ? (
                      <HorizontalBar data={dataBarras} options={barrasOptions} />
                    ) : (
                      <p style={{ textAlign: "center" }}>Sin eventos logísticos para mostrar</p>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Grid.Col>

            {/* Gráfica circular general */}
            <Grid.Col sm={12} lg={6}>
              <Card title="Movimientos en general (rangos de tiempo)">
                <Card.Body>
                  {generalData ? (
                    <>
                      <Doughnut data={generalData} />
                      <div style={{ textAlign: "center", marginTop: "10px" }}>
                        Total de movimientos: {general.totalMovimientos || 0}
                      </div>
                    </>
                  ) : (
                    <p style={{ textAlign: "center" }}>Sin datos generales</p>
                  )}
                </Card.Body>
              </Card>
            </Grid.Col>

            {/* Tabla resumen por evento logístico */}
            <Grid.Col sm={12} lg={6}>
              <Card title="Detalle por evento logístico">
                <Table cards striped responsive className="table-vcenter">
                  <Table.Header>
                    <Table.ColHeader>Evento logístico</Table.ColHeader>
                    <Table.ColHeader>Total</Table.ColHeader>
                    <Table.ColHeader>Porcentaje</Table.ColHeader>
                  </Table.Header>
                  <Table.Body>
                    {barrasData.map((e, index) => (
                      <Table.Row key={index}>
                        <Table.Col>{e.evento}</Table.Col>
                        <Table.Col>{e.total}</Table.Col>
                        <Table.Col>
                          {totalEventos ? ((100 * (e.total || 0)) / totalEventos).toFixed(1) : 0}%
                        </Table.Col>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Card>
            </Grid.Col>

            <Grid.Col sm={12} lg={12}>
              <div style={{ marginTop: "10px" }}>
                Movimientos mostrados: {movimientosFiltrados.length}
              </div>
            </Grid.Col>

            {/* ============ TABLERO POR ACV (desempeño por usuario) ============ */}
            {/* Se muestra al final, después de la tabla "Detalle por evento logístico". */}
            <Grid.Col sm={12} lg={12}>
              <Card title="Tablero por ACV">
                <Card.Body>
                  {acvOrdenado.length > 0 ? (
                    <Grid.Row>
                      <Grid.Col sm={12} lg={10}>
                        <div style={{ height: Math.max(220, acvOrdenado.length * 42) + "px" }}>
                          <HorizontalBar data={dataAcv} options={acvOptions} />
                        </div>
                      </Grid.Col>
                      <Grid.Col sm={12} lg={2}>
                        <Table cards striped responsive className="table-vcenter">
                          <Table.Header>
                            <Table.ColHeader>Usuario</Table.ColHeader>
                            <Table.ColHeader>%</Table.ColHeader>
                          </Table.Header>
                          <Table.Body>
                            {acvOrdenado.map((u, index) => (
                              <Table.Row key={index}>
                                <Table.Col>{nombreUsuario(u)}</Table.Col>
                                <Table.Col>{(acvPorcentajes[index] || 0).toFixed(2)}%</Table.Col>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      </Grid.Col>
                    </Grid.Row>
                  ) : (
                    <p style={{ textAlign: "center" }}>Sin desempeño por usuario para mostrar</p>
                  )}
                </Card.Body>
              </Card>
            </Grid.Col>
          </>
        ) : null}
      </div>
    </SiteWrapper>
  );
};

export default TableroCapturaDetalle;
